
import jsPDF from 'jspdf';
import { PDF_STYLES, TABLE_CONFIG } from './pdfStyles';
import { PDFReportData } from '@/types/pdf';
import { formatCurrency } from '../formatters';
import { normalizeCountryCode } from '../countries';

export const addBooksBreakdownTable = (pdf: jsPDF, data: PDFReportData, yPosition: number): number => {
  const { margin, fonts, lineWidth } = PDF_STYLES;
  
  // Use abbreviated headers from TABLE_CONFIG
  const headers = TABLE_CONFIG.headers;
  const colWidths = TABLE_CONFIG.colWidths;
  const colPositions = TABLE_CONFIG.colPositions;
  const rightAlignColumns = TABLE_CONFIG.rightAlignColumns;

  // Calculate totals using new logic - handle 0 values properly
  const totalPreviousRoyaltyIncome = data.reports.reduce((sum, r) => sum + (r.netSales ?? 0), 0);
  const totalGrossSales = data.reports.reduce((sum, r) => sum + (r.grossSales || r.amount || 0), 0);
  const totalRoyaltyAmount = data.reports.reduce((sum, r) => sum + (r.royaltyAmount || r.amount || 0), 0);
  
  // Table title
  pdf.setFontSize(fonts.subtitle);
  pdf.setFont(undefined, 'bold');
  pdf.text('Books Breakdown', margin, yPosition);
  yPosition += 10;
  
  // Table headers
  pdf.setFontSize(fonts.normal);
  pdf.setFont(undefined, 'bold');
  
  headers.forEach((header, index) => {
    const text = String(header || '');
    if (rightAlignColumns.includes(index)) {
      pdf.text(text, colPositions[index] + colWidths[index] - 2, yPosition, { align: 'right' });
    } else {
      pdf.text(text, colPositions[index], yPosition);
    }
  });
  
  // Underline headers - respect right margin
  pdf.setLineWidth(lineWidth.thin);
  const tableWidth = colPositions[colPositions.length - 1] + colWidths[colWidths.length - 1] - margin;
  pdf.line(margin, yPosition + 2, margin + tableWidth, yPosition + 2);
  yPosition += 6;
  
  // Table content
  pdf.setFont(undefined, 'normal');
  data.reports.forEach((report) => {
    const rowData = [
      report.books?.title || 'Unknown',
      (report.bookFormat || 'paperback').charAt(0).toUpperCase() + (report.bookFormat || 'paperback').slice(1),
      normalizeCountryCode(report.country || 'US'),
      String(report.unitsSold || report.salesCount || 0),
      formatCurrency(report.grossSales || report.amount || 0),
      formatCurrency(report.netSales ?? 0), // Use nullish coalescing to properly handle 0 values
      formatCurrency(report.royaltyAmount || report.amount || 0)
    ];
    
    // Calculate row height based on title text wrapping
    const titleMaxWidth = colWidths[0] - 2;
    const title = String(rowData[0]);
    const titleLines = pdf.splitTextToSize(title, titleMaxWidth);
    const rowHeight = Math.max(5, titleLines.length * 4);
    
    rowData.forEach((data, index) => {
      const text = String(data || '');
      
      if (index === 0) {
        // Handle title with text wrapping
        titleLines.forEach((line: string, lineIndex: number) => {
          pdf.text(line, colPositions[index], yPosition + (lineIndex * 4));
        });
      } else {
        // Handle other columns normally
        if (rightAlignColumns.includes(index)) {
          pdf.text(text, colPositions[index] + colWidths[index] - 2, yPosition, { align: 'right' });
        } else {
          pdf.text(text, colPositions[index], yPosition);
        }
      }
    });
    
    yPosition += rowHeight;
  });
  
  // Total row with line above
  yPosition += 2;
  pdf.setLineWidth(lineWidth.thin);
  pdf.line(margin, yPosition, margin + tableWidth, yPosition);
  yPosition += 6;
  
  pdf.setFont(undefined, 'bold');
  pdf.text('TOTAL', colPositions[0], yPosition);
  pdf.text(String(data.totalUnitsSold), colPositions[3] + colWidths[3] - 2, yPosition, { align: 'right' });
  pdf.text(formatCurrency(totalGrossSales), colPositions[4] + colWidths[4] - 2, yPosition, { align: 'right' });
  pdf.text(formatCurrency(totalPreviousRoyaltyIncome), colPositions[5] + colWidths[5] - 2, yPosition, { align: 'right' });
  pdf.text(formatCurrency(totalRoyaltyAmount), colPositions[6] + colWidths[6] - 2, yPosition, { align: 'right' });
  
  return yPosition;
};
